import { changed, noOp, type Command, type CommandExecution } from './commands';
import { createId, type EditorDocument } from './model';
import type { Point } from './geometry';

export const NOTES_EXTENSION = 'maipo:notes';

export interface DocumentNote {
  id: string;
  x: number;
  y: number;
  text: string;
  author: string;
  color: 'yellow' | 'blue' | 'green' | 'pink';
  resolved: boolean;
  createdAt: string;
  /**
   * Optional so notes written before this field existed still load. Absent
   * means "never edited since it was pinned".
   */
  updatedAt?: string;
}

function validNote(value: unknown): value is DocumentNote {
  if (!value || typeof value !== 'object') return false;
  const note = value as Partial<DocumentNote>;
  return typeof note.id === 'string' && Number.isFinite(note.x) && Number.isFinite(note.y)
    && typeof note.text === 'string' && typeof note.author === 'string'
    && (note.color === 'yellow' || note.color === 'blue' || note.color === 'green' || note.color === 'pink')
    && typeof note.resolved === 'boolean' && typeof note.createdAt === 'string'
    && (note.updatedAt === undefined || typeof note.updatedAt === 'string');
}

export function documentNotes(document: EditorDocument): DocumentNote[] {
  const value = document.extensions[NOTES_EXTENSION];
  return Array.isArray(value) ? value.filter(validNote).map((note) => structuredClone(note)) : [];
}

export function createDocumentNote(document: EditorDocument, point: Point): DocumentNote {
  return {
    id: createId('note'),
    x: Math.max(0, Math.min(document.width, point.x)),
    y: Math.max(0, Math.min(document.height, point.y)),
    text: '',
    author: 'Local reviewer',
    color: 'yellow',
    resolved: false,
    createdAt: new Date().toISOString(),
  };
}

export class SetDocumentNotesCommand implements Command {
  readonly label: string;
  private before?: unknown;
  private hadBefore = false;

  constructor(private readonly notes: readonly DocumentNote[], label = 'Update notes') {
    this.label = label;
  }

  retainedBytes(): number {
    return 128 + JSON.stringify(this.notes).length * 2 + JSON.stringify(this.before ?? null).length * 2;
  }

  execute(document: EditorDocument): CommandExecution {
    if (JSON.stringify(documentNotes(document)) === JSON.stringify(this.notes)) return noOp();
    if (this.before === undefined) {
      this.hadBefore = Object.hasOwn(document.extensions, NOTES_EXTENSION);
      this.before = structuredClone(document.extensions[NOTES_EXTENSION] ?? null);
    }
    if (this.notes.length) document.extensions[NOTES_EXTENSION] = structuredClone(this.notes);
    else delete document.extensions[NOTES_EXTENSION];
    return changed();
  }

  undo(document: EditorDocument): void {
    if (this.hadBefore) document.extensions[NOTES_EXTENSION] = structuredClone(this.before);
    else delete document.extensions[NOTES_EXTENSION];
  }
}
